/**
 * Low-level dataLayer access. Nothing outside this folder should touch
 * `window.dataLayer` directly — see events.ts for the functions components call.
 *
 * The GTM container reads exclusively from the shape pushed here (the
 * "dataLayer contract"). Renaming or restructuring a field silently breaks
 * every downstream platform tag, so treat changes as an API change and flag
 * them to Marketing before deploying.
 */

export const CURRENCY = "SAR";

export type ItemCategory = "mobile_plan" | "travel_esim" | "home_router";

export interface TrackingItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category: ItemCategory;
  item_variant?: string;
  /** Post-discount price of the item itself, 2 decimals. */
  price: number;
  discount?: number;
  quantity?: number;
  index?: number;
}

export interface Ecommerce {
  currency?: string;
  value?: number;
  transaction_id?: string;
  tax?: number;
  payment_type?: string;
  items: TrackingItem[];
}

const layer = (): any[] => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/** Rounds to 2 decimals and returns a Number — platforms reject stringified values. */
export const money = (value: any): number => {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

/**
 * Shared dedup key between the browser event and its server-side counterpart
 * (Meta `event_id`, TikTok `event_id`, Snap `client_dedup_id`). For purchase we
 * pass the order id so the payment webhook can reproduce it; everything else
 * gets a throwaway uuid.
 */
export const newEventId = (): string => {
  const c: any = window.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return "evt-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
};

/** Push a non-commerce event. */
export const pushEvent = (event: string, payload: Record<string, any> = {}) => {
  layer().push({ event, event_id: payload.event_id || newEventId(), ...payload });
};

/**
 * Push a commerce event. The `{ ecommerce: null }` reset is mandatory per
 * Google's GA4 guidance — without it the previous event's item array bleeds
 * into this one, which is the single most common cause of wrong item data.
 */
export const pushEcommerce = (
  event: string,
  ecommerce: Ecommerce,
  payload: Record<string, any> = {}
) => {
  layer().push({ ecommerce: null });
  layer().push({
    event,
    event_id: payload.event_id || newEventId(),
    ...payload,
    ecommerce,
  });
};
